<template>
    <router-view @open-model="expandModel" />

    <not-found-view v-if="error" :error500="true"></not-found-view>

    <div v-else :class="['game', {'hidden': $route.name != 'game' && !fullscreenModel}]">
        <div class="grid grid--padding">
            <div class="game__header">
                <button v-if="currentPhaseSavedMaterials.data.length" @click="toggleModal" :class="['btn btn--game', {'highlight': tooltipsActive == 3}]">
                    <span>{{ currentPhaseData.key }}: </span>
                    <template v-if="currentPhaseSavedMaterials.data.length > 1">
                        {{ $t('game.text.number-of-materials') }} {{ currentPhaseSavedMaterials.data.length }}
                    </template>
                    <template v-else>
                        {{ currentPhaseSavedMaterials.data[0].name }}
                    </template>
                </button>

                <div v-if="!tooltipsSeenAll && tooltipsActive == 3" class="tooltip tooltip--top animate__animated animate__bounceInUp">
                    <div class="tooltip__progress">{{ tooltipsActive }} {{ $t('game.tooltip.text.of')}} {{ tooltipsTotalCount }}</div>
                    <div class="tooltip__text">{{ $t('game.tooltip.material-instruction') }}</div>
                    <button @click="updateTooltips" class="btn btn--small btn--persimmon">{{ $t('button.next') }}</button>
                </div>
            </div>

            <div v-if="!savedMaterials[0].data.length" class="game__container game__container--empty">
                <img src="@/assets/images/start-game.svg" :alt="$t('game.empty.title')" />
                <h4>{{ $t('game.empty.title') }}</h4>
                <p>{{ $t('game.empty.text') }}</p>
                <router-link v-if="currentPhaseData" :to="{name: 'game-menu', params: {phaseId: currentPhaseData.id, phaseKey: currentPhaseData.name, phaseMultichoice: currentPhaseData.multipleChoices, editing: false}}" class="btn btn--primary">
                    {{ $t('button.choose') }} {{ currentPhaseData.name }}
                </router-link>
            </div>

            <div v-show="savedMaterials[currentPhase].data.length" :class="['game__container']">
                <keep-alive>
                    <house-model :expanded="fullscreenModel" @close-model="collapseModel"></house-model>
                </keep-alive>
                <learn-more v-if="currentPhaseSavedMaterials.data.length && currentPhaseData.tooltips" :tooltips="currentPhaseData.tooltips"></learn-more>
            </div>

            <div v-if="savedMaterials.length" class="game__actions">
                <button @click="goToPreviousPhase" class="btn btn--default">
                    <span v-if="prevPhase">&lt;</span>
                    <span v-if="prevPhase">{{ prevPhase }}</span>
                </button>

                <router-link v-if="currentPhaseSavedMaterials.data.length" :to="{name: 'game-menu', params: {phaseId: currentPhaseData.id, phaseKey: currentPhaseData.name, phaseMultichoice: currentPhaseData.multipleChoices, editing: false}}" :class="['btn btn--primary btn--add', {'highlight': tooltipsActive == 1}]">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24.25 9.17501L20.825 5.75001C20.378 5.33013 19.7922 5.0892 19.1792 5.07307C18.5661 5.05694 17.9685 5.26672 17.5 5.66251L6.25 16.9125C5.84596 17.32 5.59439 17.854 5.5375 18.425L5 23.6375C4.98317 23.8206 5.00692 24.0051 5.06958 24.178C5.13224 24.3508 5.23226 24.5077 5.3625 24.6375C5.4793 24.7534 5.61782 24.845 5.77012 24.9072C5.92242 24.9694 6.0855 25.001 6.25 25H6.3625L11.575 24.525C12.146 24.4681 12.68 24.2165 13.0875 23.8125L24.3375 12.5625C24.7742 12.1012 25.0101 11.4856 24.9937 10.8507C24.9773 10.2157 24.7099 9.61314 24.25 9.17501ZM20 13.35L16.65 10L19.0875 7.50001L22.5 10.9125L20 13.35Z" fill="white"/>
                    </svg>
                </router-link>

                <button v-if="!finishGame && currentPhaseSavedMaterials.data.length" @click="goToNextPhase" :class="['btn btn--default', {'disabled': !currentPhaseSavedMaterials.data.length && tooltipsSeenAll, 'highlight': tooltipsActive == 2}]">
                    <span v-if="nextPhase">{{ nextPhase }}</span>
                    <span v-if="nextPhase">&gt;</span>
                </button>

                <router-link v-if="finishGame" :to="{name: 'game-overview'}" :class="['btn btn--default', {'disabled': !vectaryModel}]">
                    <span>{{ $t('button.complete-game') }}</span>
                    <span>&gt;</span>
                </router-link>

                <div v-if="!tooltipsSeenAll && tooltipsActive == 1 && currentPhaseSavedMaterials.data.length" class="tooltip tooltip--middle animate__animated animate__bounceInLeft">
                    <div class="tooltip__progress">{{ tooltipsActive }} {{ $t('game.tooltip.text.of')}} {{ tooltipsTotalCount }}</div>
                    <div class="tooltip__text">{{ $t('game.tooltip.edit-instruction') }}</div>
                    <button @click="updateTooltips" class="btn btn--small btn--persimmon">{{ $t('button.next') }}</button>
                </div>

                <div v-if="!tooltipsSeenAll && tooltipsActive == 2 && currentPhaseSavedMaterials.data.length" class="tooltip tooltip--side animate__animated animate__bounceInRight">
                    <div class="tooltip__progress">{{ tooltipsActive }} {{ $t('game.tooltip.text.of')}} {{ tooltipsTotalCount }}</div>
                    <div class="tooltip__text">{{ $t('game.tooltip.phase-instruction') }}</div>
                    <button @click="updateTooltips" class="btn btn--small btn--persimmon">{{ $t('button.next') }}</button>
                </div>

                <div v-if="showSkipPhaseTooltip()" class="tooltip tooltip--side animate__animated animate__bounceInRight">
                    <div class="tooltip__title">{{ $t('game.tooltip.skip-phase-title') }}</div>
                    <div class="tooltip__text">{{ $t('game.tooltip.skip-phase-text') }} {{ getHouseBuildingPhaseListing.edges[currentPhase + 1].node.name }}!</div>
                    <button @click="closeSkipPhaseTooltip" class="btn btn--small btn--persimmon">{{ $t('button.i-understand') }}</button>
                </div>
            </div>

            <menu-modal v-if="isModalVisible" :modalClass="'menu-modal--wider'">
                <template #content>
                    <template v-for="(material, index) in currentPhaseSavedMaterials.data" :key="index">
                        <div v-if="material.thumbnail" class="menu-modal__img">
                            <img :src="`${apiUri}${material.thumbnail}`" :alt="material.name" />
                        </div>

                        <div class="menu-modal__title">{{ material.name }}</div>
                        <div class="menu-modal__desc" v-html="material.description"></div>

                        <ul class="price-list">
                            <li class="price-list__item price-list__item--highlighted">
                                <span>{{ $t('game.text.item-price') }}</span>
                                <span class="price-list__price">{{ calcSavedMaterialPrice(material.price.value) }} {{ budgetCurrency }}</span>
                            </li>
                        </ul>
                    </template>
                </template>

                <template #actions>
                    <button @click="toggleModal" class="btn">{{ $t('button.back') }}</button>
                </template>
            </menu-modal>
        </div>
    </div>
</template>

<script>
import { getHouseBuildingPhaseListing } from '@/queries/houseBuildingPhasesQueries.js';
import MenuModal from '@/components/game/MenuModal.vue';
import HouseModel from '@/components/game/HouseModel.vue';
import LearnMore from '@/components/game/LearnMore.vue';

export default {
    name: 'GameView',
    components: {
        MenuModal,
        HouseModel,
        LearnMore
    },
    data() {
        return {
            error: false,
            getHouseBuildingPhaseListing: null,
            isModalVisible: false,
            fullscreenModel: false,
            skipPhaseTooltipSeen: []
        }
    },
    apollo: {
        getHouseBuildingPhaseListing: {
            query: getHouseBuildingPhaseListing,
            error(error) {
                console.log('errors', error);
                this.error = true;
            }
        },
    },
    computed: {
        currentPhase() {
            return this.$store.getters['game/getCurrentPhase'];
        },
        currentPhaseData() {
            if (this.getHouseBuildingPhaseListing != null) {
                return this.getHouseBuildingPhaseListing.edges[this.currentPhase].node;
            } else {
                return false;
            }
        },
        nextPhase() {
            if (this.getHouseBuildingPhaseListing != null && this.currentPhase < this.totalPhaseCount) {
                if (this.checkIfSkippingPhase('next')) {
                    return this.getHouseBuildingPhaseListing.edges[this.currentPhase + 2].node.name;
                } else {
                    return this.getHouseBuildingPhaseListing.edges[this.currentPhase + 1].node.name;
                }
            } else {
                return false;
            }
        },
        prevPhase() {
            if (this.getHouseBuildingPhaseListing != null && this.currentPhase - 1 >= 0) {
                if (this.checkIfSkippingPhase('prev')) {
                    return this.getHouseBuildingPhaseListing.edges[this.currentPhase - 2].node.name;
                } else {
                    return this.getHouseBuildingPhaseListing.edges[this.currentPhase - 1].node.name;
                }
            } else {
                return false;
            }
        },
        totalPhaseCount() {
            if (this.getHouseBuildingPhaseListing != null) {
                return this.getHouseBuildingPhaseListing.totalCount - 1;
            } else {
                return false;
            }
        },
        showPhases() {
            if (this.currentPhase < this.totalPhaseCount) {
                return true;
            } else {
                return false;
            }
        },
        savedMaterials() {
            return this.$store.getters['game/getSavedMaterials'];
        },
        currentPhaseSavedMaterials() {
            const materials = this.savedMaterials.filter(phase => phase.id == this.currentPhaseData.id);

            return materials[0];
        },
        budgetCurrency() {
            return this.$store.getters['game/getBudgetCurrency'];
        },
        tooltipsTotalCount() {
            return this.$store.getters['game/getTooltipsTotalCount'];
        },
        tooltipsActive() {
            return this.$store.getters['game/getTooltipsActive'];
        },
        tooltipsSeenAll() {
            return this.$store.getters['game/getTooltipsSeenAll'];
        },
        finishGame() {
            if (this.currentPhase == this.totalPhaseCount) {
                return true;
            } else {
                return false;
            }
        },
        apiUri() {
            return process.env.VUE_APP_APOLLO_API_URI;
        },
        vectaryModel() {
            return this.$store.getters['game/getVectaryModel'];
        },
    },
    methods: {
        goToNextPhase() {
            let nextPhase;

            if (this.checkIfSkippingPhase('next')) {
                nextPhase = this.currentPhase + 2;
            } else {
                nextPhase = this.currentPhase + 1;
            }

            this.$store.commit('game/updatePhase', nextPhase);
            
            if (!this.currentPhaseSavedMaterials.data.length) {
                this.$router.push({
                    name: 'game-menu', 
                    params: {
                        phaseId: this.currentPhaseData.id, 
                        phaseKey: this.currentPhaseData.name, 
                        phaseMultichoice: this.currentPhaseData.multipleChoices,
                        editing: false
                    }
                });
            }
        },
        goToPreviousPhase() {
            let prevPhase;

            if (this.checkIfSkippingPhase('prev')) {
                prevPhase = this.currentPhase - 2;
            } else {
                prevPhase = this.currentPhase - 1;
            }
            
            this.$store.commit('game/updatePhase', prevPhase);
        },
        toggleModal() {
            this.isModalVisible = !this.isModalVisible;
        },
        calcSavedMaterialPrice(value) {
            let price = parseInt(value) * this.$store.getters['game/getSurfaceArea'];

            return Number(price).toLocaleString('hr');
        },
        closeTooltip() {
            document.body.classList.remove('overlay');
        },
        updateTooltips() {
            this.$store.commit('game/updateTooltips');
        },
        expandModel() {
            this.fullscreenModel = true;
        },
        collapseModel() {
            this.fullscreenModel = false;
        },
        checkIfSkippingPhase(phase) {
            let phaseDisabledBy;
            let skipPhase = false;

            if (phase == 'next') {
                phaseDisabledBy = this.getHouseBuildingPhaseListing.edges[this.currentPhase + 1].node.disabledByMaterial;
            } else {
                phaseDisabledBy = this.getHouseBuildingPhaseListing.edges[this.currentPhase - 1].node.disabledByMaterial;
            }

            if (phaseDisabledBy) {
                this.savedMaterials.forEach(phase => {
                    if (phase.data) {
                        phase.data.forEach(material => {
                            phaseDisabledBy.forEach(mat => {
                                if (material.uniqueId == mat.id) {
                                    skipPhase = true;
                                }
                            });
                        });
                    }
                });
            }

            return skipPhase ? true : false;
        },
        showSkipPhaseTooltip() {
            if (this.currentPhase < this.totalPhaseCount) {
                if (this.checkIfSkippingPhase('next') && !this.skipPhaseTooltipSeen.includes(this.currentPhaseData.id) && this.$route.name == 'game') {
                    document.body.classList.add('overlay');
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },
        closeSkipPhaseTooltip() {
            this.skipPhaseTooltipSeen.push(this.currentPhaseData.id);
            document.body.classList.remove('overlay');
        }
    },
    updated() {
        if (!this.tooltipsSeenAll && this.currentPhaseSavedMaterials.data.length) {
            document.body.classList.add('overlay');
        }
    },
    watch: {
        getHouseBuildingPhaseListing() {
            if (this.getHouseBuildingPhaseListing && this.savedMaterials.length == 0) {
                this.getHouseBuildingPhaseListing.edges.forEach(phase => {
                    let data = {
                        id: phase.node.id,
                        key: phase.node.key,
                        multipleChoices: phase.node.multipleChoices,
                        disabledByMaterial: phase.node.disabledByMaterial,
                        data: []
                    }

                    this.$store.commit('game/savePhases', data);
                });
            }
        }
    },
    beforeRouteUpdate(_to, _from, next) {
        this.closeTooltip();
        next();
    }
}
</script>
